import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'Douleutaras/redux/hooks';

import {
  FloatingSupportState,
  ResolvedSupportSurface,
  resolveFloatingSupportState,
  resolveSupportSurface
} from './surfaces';

export interface FloatingSupportWidget {
  /** Whether availability has landed — `state` is only meaningful once it has. */
  loaded: boolean;
  /** The surface this route belongs to; `undefined` where the widget has no requirement. */
  surface?: ResolvedSupportSurface;
  /** What the widget renders as; `undefined` until availability lands, or when it must not render. */
  state?: FloatingSupportState;
}

/** Route → surface → render state. Pure selection, so any consumer can ask. */
export const useFloatingSupportWidget = (): FloatingSupportWidget => {
  const { pathname, search } = useLocation();
  const support = useAppSelector(state => state.support);

  const surface = useMemo(() => resolveSupportSurface(pathname, search), [pathname, search]);

  const { loaded } = support;

  if (!surface || !loaded) {
    return { loaded, surface };
  }

  return { loaded, surface, state: resolveFloatingSupportState(support.entities, surface.policy) };
};

/**
 * Whether the floating widget is this route's support launcher (#5620): `undefined`
 * until availability lands, then a boolean. For consumers that only need that answer —
 * the boolean selector keeps them from re-rendering on unrelated `support` changes.
 */
export const useSupportLauncherActive = (): boolean | undefined => {
  const { pathname, search } = useLocation();
  const surface = useMemo(() => resolveSupportSurface(pathname, search), [pathname, search]);

  return useAppSelector(state => {
    if (!state.support.loaded) {
      return undefined;
    }

    return Boolean(surface && resolveFloatingSupportState(state.support.entities, surface.policy));
  });
};
